"use client";

import QRCode from "qrcode";
import { useEffect, useRef, useState } from "react";

type Ticket = {
  token: string; name: string; reservationType: "queue" | "time";
  queueNumber: number | null; requestedTime: string | null; status: string;
  attractionName: string; className: string; location: string; checkedInAt: string | null;
};

export function TicketView({ token }: { token: string }) {
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [error, setError] = useState("");
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    fetch(`/api/tickets/${encodeURIComponent(token)}`)
      .then((response) => response.json().then((data) => ({ response, data: data as { error?: string; ticket?: Ticket } })))
      .then(({ response, data }) => {
        if (!response.ok) throw new Error(data.error);
        if (!data.ticket) throw new Error("予約券を表示できません。");
        setTicket(data.ticket);
      })
      .catch((caught) => setError(caught instanceof Error ? caught.message : "予約券を表示できません。"));
  }, [token]);

  useEffect(() => {
    if (!ticket || !canvasRef.current) return;
    QRCode.toCanvas(canvasRef.current, `${window.location.origin}/ticket/${ticket.token}`, {
      width: 248, margin: 2, color: { dark: "#161616", light: "#ffffff" }, errorCorrectionLevel: "H",
    });
  }, [ticket]);

  if (error) return <main className="ticket-page"><div className="empty-card"><strong>{error}</strong><a className="primary-button" href="/">企画一覧へ</a></div></main>;
  if (!ticket) return <main className="ticket-page"><div className="empty-card"><span className="spinner" />予約券を準備しています</div></main>;

  const checkedIn = ticket.status === "checked_in" || ticket.status === "completed";
  return (
    <main className="ticket-page">
      <a className="brand ticket-brand" href="/"><span className="brand-mark">文</span><span>文化祭 <small>QUEUE PASS</small></span></a>
      <section className="ticket-success">
        <div className="success-icon">✓</div>
        <p className="eyebrow"><span>✦</span> RESERVATION COMPLETE</p>
        <h1>{checkedIn ? "受付済みです！" : "予約が完了しました！"}</h1>
        <p>{checkedIn ? "スタッフが受付を確認しました。" : "下の予約券を、受付でご提示ください。"}</p>
      </section>
      <section className={checkedIn ? "digital-ticket checked" : "digital-ticket"}>
        <div className="ticket-head"><span>{ticket.className}</span><small>CULTURE FESTIVAL / ADMIT ONE</small></div>
        <div className="ticket-content">
          <div className="ticket-details">
            <p>ATTRACTION</p><h2>{ticket.attractionName}</h2>
            <dl>
              <div><dt>お名前</dt><dd>{ticket.name}</dd></div>
              <div><dt>場所</dt><dd>{ticket.location || "校内"}</dd></div>
            </dl>
            <div className="number-panel">
              <small>{ticket.reservationType === "queue" ? "予約番号" : "予約時刻"}</small>
              <strong>{ticket.reservationType === "queue" ? `#${String(ticket.queueNumber).padStart(3, "0")}` : ticket.requestedTime}</strong>
            </div>
          </div>
          <div className="qr-panel"><canvas ref={canvasRef} aria-label="受付用QRコード" /><span>{ticket.token.slice(0, 12)}</span></div>
        </div>
        {checkedIn && <div className="checked-stamp">受付済</div>}
      </section>
      <section className="screenshot-notice"><span>▣</span><div><strong>この画面をスクリーンショットしてください</strong><p>通信が不安定な場合に備えて、QRコードが見える状態で保存してください。</p></div></section>
      <div className="ticket-actions"><button onClick={() => window.print()}>予約券を印刷・保存</button><a href="/reserve">ほかの企画を見る</a></div>
      <p className="ticket-footnote">QRコードはこの企画専用です。受付スタッフ以外には共有しないでください。</p>
    </main>
  );
}
